import { hierarchy, pack } from 'd3-hierarchy';
import 'd3-transition';
import { pointer, select } from 'd3-selection';
import type { Selection } from 'd3-selection';
import { zoom, zoomTransform } from 'd3-zoom';
import type { ZoomTransform } from 'd3-zoom';

import { createBubbleChipLayer } from './bubbleChips';
import { type BubbleEmphasisContainer, type ChipElement } from '../bubbleEmphasis';
import { infraBubbleStyles as s, volumeBubbleStyles as vb } from '../bubbleDimColors';
import {
  isInfraHierarchyType,
  setInfraHoverState,
  type BubbleInfraContainer,
  type InfraVisualSet,
} from '../bubbleInfraStyles';
import {
  fillForBubble,
  fillOpacityForBubble,
  HIERARCHY_TYPES,
  hierarchyRingHighStroke,
  hierarchyRings,
  usesOutsideStatusChips,
  isVolumeManaged,
  managedInnerRatio,
  packValueForNode,
  resolveInsideCenterLabelLayout,
  strokeDashForBubble,
  strokeForBubble,
  strokeOpacityForBubble,
  strokeWidthForBubble,
} from '../../../lib/bubble/bubbleHelpers';
import type { PackLayoutNode } from '../../../lib/bubble/packNodeTypes';
import { ec2InstancesLabel } from '../../../lib/classification';
import {
  type BubbleSvgSelection,
  type BubbleZoomBehavior,
  type ChipParentSelection,
  type CircleNodeSelection,
  type ContentGroupSelection,
  type LabelGroupSelection,
} from '../../../lib/d3/d3SelectionTypes';
import {
  clearBubbleCanvasDrillUpClass,
  syncBubbleCanvasDrillUpClass,
} from './bubbleCanvasCursor';
import { shouldDrillUpFromCanvasClick } from './bubbleDrillUp';
import { isBubbleNodeClickable } from './bubbleClickDispatch';
import { bindBubbleCircleEvents } from './bubbleNodeHandlers';
import { bubbleNodeKey } from './bubbleNodeKey';
import {
  bubbleTransitionMs,
  resolveCanDrillUp,
  type BubbleRenderOptions,
  type BubbleZoomControls,
} from './bubbleRenderOptions';
import {
  prepareBubbleTransition,
  runBubbleFlipTransition,
  snapshotLiveBubblePositions,
} from './bubbleTransitions';
import { createBubbleVolumeLabelLayer } from './bubbleVolumeLabels';
import { volumeBubbleLabelLayout } from '../../../lib/bubble/volumeBubbleLabels';
import {
  bubbleZoomDragFilter,
  clampZoomTransform,
  computeBubbleScaleExtent,
  createBubbleZoomControls,
  deleteZoomCache,
  getSavedZoomTransform,
  rememberRenderZoomKey,
  setZoomCache,
  snapshotZoomBeforeRender,
  wheelZoomFactor,
  zoomCacheKey,
  zoomCanZoomIn,
  zoomCanZoomOut,
} from './bubbleZoom';
import { formatBytes } from '../../../lib/formatBytes';
import { explorationCanvasEmptyStateHtml } from '../../explorationCanvasChrome';
import type { HierarchyNode, ExplorationVolume } from '../../../types/exploration';

export type { BubbleRenderOptions, BubbleZoomControls } from './bubbleRenderOptions';

/** Dev toggle for structural hierarchy glow halos (cluster, tag, region, …). */
const bubbleHalosEnabled = false;

export function renderBubbleView(
  container: HTMLElement,
  root: HierarchyNode,
  options: BubbleRenderOptions
) {
  const w = container.clientWidth;
  const h = container.clientHeight;
  const currentZoomKey = zoomCacheKey(options, w, h);
  let bubbleZoom: BubbleZoomBehavior | null = null;
  let bubbleSvg: BubbleSvgSelection | null = null;

  const clearZoomControls = () => {
    if (options.zoomControlsRef) options.zoomControlsRef.current = null;
  };

  snapshotZoomBeforeRender(container);
  let { posCache, posCacheContext } = snapshotLiveBubblePositions(
    container,
    options.view,
    options.canvasId
  );

  container.innerHTML = '';
  if (!w || !h) {
    clearBubbleCanvasDrillUpClass(container);
    clearZoomControls();
    return () => {
      clearBubbleCanvasDrillUpClass(container);
    };
  }

  if (!root.children || root.children.length === 0) {
    options.hideTooltip();
    container.innerHTML = explorationCanvasEmptyStateHtml();
    clearBubbleCanvasDrillUpClass(container);
    clearZoomControls();
    return () => {
      clearBubbleCanvasDrillUpClass(container);
    };
  }

  const d3root = hierarchy(root)
    .sum((d) => (d.children ? 0 : packValueForNode(d)))
    .sort((a, b) => (b.value || 0) - (a.value || 0)) as PackLayoutNode;

  const size = Math.min(w, h);
  pack<HierarchyNode>()
    .size([size, size])
    .padding((d) => {
      if (d.depth === 0) return 36;
      const kids = d.children || [];
      const tightKids =
        kids.length > 0 &&
        kids.every(
          (k) => k.data.type === 'volume' || k.data.type === 'status' || k.data.type === 'reason'
        );
      if (tightKids) return d.depth === 1 ? 4 : 2;
      return d.depth === 1 ? 14 : d.depth === 2 ? 6 : 1;
    })(d3root);

  syncBubbleCanvasDrillUpClass(container, resolveCanDrillUp(options));

  const svg = select(container)
    .append('svg')
    .attr('viewBox', `0 0 ${size} ${size}`)
    .attr('width', w)
    .attr('height', h)
    .attr('preserveAspectRatio', 'xMidYMid meet')
    .on('contextmenu', (e: MouseEvent) => {
      e.preventDefault();
    })
    .on('click', (e: MouseEvent) => {
      if (e.button !== 0) return;
      if (resolveCanDrillUp(options) && shouldDrillUpFromCanvasClick(e.target)) {
        options.onDrillUp();
      }
    }) as unknown as BubbleSvgSelection;

  svg.append('defs').html(s.svgFilterDefs);
  const contentG = svg.append('g').attr('class', 'bubble-content') as unknown as ContentGroupSelection;

  const containerFit = Math.min(w, h) > 0 ? Math.min(w, h) : 1;
  const invFit = size / containerFit;

  const HALO_GAP_PX = 0;
  const HOVER_RIM_WIDTH = 1;
  function screenPxToVb(px: number, k: number) {
    return (px * invFit) / k;
  }
  function hoverRimCenterRadius(
    bubbleR: number,
    haloStrokePx: number,
    hoverStrokePx: number,
    k: number
  ) {
    const haloOuter = bubbleR + screenPxToVb(haloStrokePx / 2, k);
    return haloOuter + screenPxToVb(HALO_GAP_PX, k) + screenPxToVb(hoverStrokePx / 2, k);
  }
  function effectiveHaloWidth(bubbleR: number, baseWidth: number, k = 1) {
    const screenR = (bubbleR * k) / invFit;
    if (screenR < 12) return Math.max(4, baseWidth * 0.5);
    if (screenR < 24) return Math.max(6, baseWidth * 0.75);
    return baseWidth;
  }
  function hoverRimRadius(bubbleR: number, baseWidth: number, k = 1) {
    const hw = effectiveHaloWidth(bubbleR, baseWidth, k);
    return hoverRimCenterRadius(bubbleR, hw, HOVER_RIM_WIDTH, k);
  }
  function currentZoomK() {
    return bubbleSvg && bubbleSvg.node() ? zoomTransform(bubbleSvg.node()!).k : 1;
  }
  function isHaloHierarchy(d: PackLayoutNode) {
    return bubbleHalosEnabled && HIERARCHY_TYPES.has(d.data.type) && d.data.type !== 'status';
  }

  const chips = createBubbleChipLayer({ invFit, contentG, options });
  const {
    appendChip,
    appendBottomSizeChip,
    applyChipLayoutForK,
    applyTextCounterScale,
    getHierChipIconNode,
    measureTextWidth,
    chipLayout,
  } = chips;

  const nodes = d3root.descendants().slice(1) as PackLayoutNode[];
  const transitionCtx = `${options.view}|${options.canvasId}`;
  const transition = prepareBubbleTransition(nodes, posCache, posCacheContext, transitionCtx);
  posCacheContext = transition.posCacheContext;
  const { useTransition, startPos } = transition;

  const HALO_OPACITY_LOW = bubbleHalosEnabled ? 0.22 : 0;
  const HALO_OPACITY_HIGH = bubbleHalosEnabled ? 0.42 : 0;
  const HALO_WIDTH_LOW = bubbleHalosEnabled ? 10 : 0;
  const _haloByNode = new Map<PackLayoutNode, Element>();
  const _hoverRimByNode = new Map<PackLayoutNode, Element>();
  const haloNodes = nodes.filter(isHaloHierarchy);
  const hoveredHaloRef: { current: { node: PackLayoutNode; bubbleEl: Element } | null } = {
    current: null,
  };

  function applyHierarchyHaloState(
    d: PackLayoutNode,
    bubbleEl: Element | null,
    hovered: boolean,
    k: number
  ) {
    if (!isHaloHierarchy(d)) return;
    const haloW = effectiveHaloWidth(d.r, HALO_WIDTH_LOW, k);
    const halo = _haloByNode.get(d);
    if (halo) {
      halo.setAttribute('stroke-opacity', String(hovered ? HALO_OPACITY_HIGH : HALO_OPACITY_LOW));
      halo.setAttribute('stroke-width', String(haloW));
      halo.setAttribute('r', String(d.r));
    }
    const rim = _hoverRimByNode.get(d);
    if (rim) {
      if (hovered) {
        rim.setAttribute('stroke', hierarchyRingHighStroke);
        rim.setAttribute('stroke-opacity', '1');
        rim.setAttribute('stroke-width', String(HOVER_RIM_WIDTH));
      } else {
        rim.setAttribute('stroke-opacity', '0');
      }
      rim.setAttribute('r', String(hoverRimRadius(d.r, HALO_WIDTH_LOW, k)));
    }
    if (bubbleEl) {
      if (hovered) {
        bubbleEl.setAttribute('stroke-opacity', '0');
      } else {
        const ring = hierarchyRings[d.data.type];
        if (ring) {
          bubbleEl.setAttribute('stroke', ring.ringStroke);
          bubbleEl.setAttribute('stroke-width', String(strokeWidthForBubble(d)));
          bubbleEl.setAttribute('stroke-opacity', '1');
        }
      }
    }
  }

  function applyHaloLayoutForK(k: number) {
    if (!bubbleHalosEnabled || haloNodes.length === 0) return;
    haloSel.attr('stroke-width', (d) => effectiveHaloWidth(d.r, HALO_WIDTH_LOW, k));
    hoverRimSel.attr('r', (d) => hoverRimRadius(d.r, HALO_WIDTH_LOW, k));
    if (hoveredHaloRef.current) {
      applyHierarchyHaloState(
        hoveredHaloRef.current.node,
        hoveredHaloRef.current.bubbleEl,
        true,
        k
      );
    }
  }

  const haloStagingG = contentG.append('g');
  const haloSel = haloStagingG
    .selectAll<SVGCircleElement, PackLayoutNode>('circle.bubble-halo')
    .data(haloNodes, bubbleNodeKey)
    .join('circle')
    .attr('class', 'bubble-halo')
    .attr('cx', (d) => startPos(d).cx)
    .attr('cy', (d) => startPos(d).cy)
    .attr('r', (d) => startPos(d).r)
    .attr('fill', 'none')
    .attr('vector-effect', 'non-scaling-stroke')
    .attr('stroke', hierarchyRingHighStroke)
    .attr('stroke-opacity', HALO_OPACITY_LOW)
    .attr('stroke-width', (d) => effectiveHaloWidth(startPos(d).r, HALO_WIDTH_LOW, currentZoomK()))
    .attr('pointer-events', 'none');
  haloSel.each(function (this: SVGCircleElement, d) {
    _haloByNode.set(d, this);
  });

  const hoverRimStagingG = contentG.append('g');
  const hoverRimSel = hoverRimStagingG
    .selectAll<SVGCircleElement, PackLayoutNode>('circle.bubble-hover-rim')
    .data(haloNodes, bubbleNodeKey)
    .join('circle')
    .attr('class', 'bubble-hover-rim')
    .attr('cx', (d) => startPos(d).cx)
    .attr('cy', (d) => startPos(d).cy)
    .attr('r', (d) => hoverRimRadius(startPos(d).r, HALO_WIDTH_LOW, currentZoomK()))
    .attr('fill', 'none')
    .attr('vector-effect', 'non-scaling-stroke')
    .attr('stroke', hierarchyRingHighStroke)
    .attr('stroke-opacity', 0)
    .attr('stroke-width', HOVER_RIM_WIDTH)
    .attr('pointer-events', 'none');
  hoverRimSel.each(function (this: SVGCircleElement, d) {
    _hoverRimByNode.set(d, this);
  });

  const bubbleG = contentG
    .append('g')
    .attr('class', 'bubbles-main')
    .selectAll<SVGCircleElement, PackLayoutNode>('circle')
    .data(nodes)
    .join('circle')
    .attr('cx', (d) => startPos(d).cx)
    .attr('cy', (d) => startPos(d).cy)
    .attr('r', (d) => startPos(d).r)
    .attr('vector-effect', 'non-scaling-stroke')
    .attr('fill', (d) => fillForBubble(d))
    .attr('fill-opacity', (d) => fillOpacityForBubble(d))
    .attr('stroke', (d) => strokeForBubble(d, options.view))
    .attr('stroke-opacity', (d) => strokeOpacityForBubble(d))
    .attr('stroke-width', (d) => strokeWidthForBubble(d))
    .attr('stroke-dasharray', (d) => strokeDashForBubble(d))
    .attr('role', (d) => (d.data._isOverflow ? 'button' : null))
    .attr('aria-label', (d) =>
      d.data._isOverflow && d.data._hidden != null ? `Show ${d.data._hidden} more volumes` : null
    )
    .attr('cursor', (d) =>
      isBubbleNodeClickable(options.location, options.view, d, {
        secondary: options.secondary,
        tagKey: options.tagKey,
      })
        ? 'pointer'
        : 'default'
    )
    .attr('pointer-events', (d) => (d.data.type === 'volume' ? 'all' : null));

  const managedNodes = nodes.filter(
    (d) => d.data.type === 'volume' && isVolumeManaged(d.data.volume)
  );
  const managedSel = contentG
    .append('g')
    .attr('pointer-events', 'none')
    .selectAll<SVGCircleElement, PackLayoutNode>('circle')
    .data(managedNodes)
    .join('circle')
    .attr('cx', (d) => startPos(d).cx)
    .attr('cy', (d) => startPos(d).cy)
    .attr('r', (d) => startPos(d).r * managedInnerRatio(d.data.volume as ExplorationVolume))
    .attr('vector-effect', 'non-scaling-stroke')
    .attr('fill', vb.volume.managed.innerFill)
    .attr('stroke', vb.volume.managed.innerStroke)
    .attr('stroke-width', 1);

  const _managedInnerByNode = new Map<PackLayoutNode, Element>();
  managedSel.each(function (this: SVGCircleElement, d) {
    _managedInnerByNode.set(d, this);
  });

  const applyInfraHover = (node: PackLayoutNode | null, hovered: boolean) => {
    setInfraHoverState(container, node, hovered, options.view);
  };
  options.applyInfraHover = applyInfraHover;
  (container as BubbleInfraContainer).__applyInfraHover = applyInfraHover;

  bindBubbleCircleEvents({
    bubbleG: bubbleG as unknown as CircleNodeSelection,
    options,
    isHaloHierarchy,
    applyHierarchyHaloState,
    hoveredHaloRef,
    currentZoomK,
    managedInnerByNode: _managedInnerByNode,
    applyInfraHover,
  });

  const _bubbleByNode = new Map<PackLayoutNode, Element>();
  bubbleG.each(function (this: SVGCircleElement, d) {
    _bubbleByNode.set(d, this);
    if (isInfraHierarchyType(d.data.type)) {
      this.setAttribute('filter', s.bubble.shadowFilter.default);
    }
  });
  options.getBubbleCircleForNode = (node) => _bubbleByNode.get(node) ?? null;

  nodes.forEach((d) => {
    if (!isHaloHierarchy(d)) return;
    const halo = _haloByNode.get(d);
    const rim = _hoverRimByNode.get(d);
    const bub = _bubbleByNode.get(d);
    if (halo && bub?.parentNode) bub.parentNode.insertBefore(halo, bub);
    if (rim && bub?.parentNode) {
      if (bub.nextSibling) bub.parentNode.insertBefore(rim, bub.nextSibling);
      else bub.parentNode.appendChild(rim);
    }
  });
  if (!haloStagingG.node()?.childElementCount) haloStagingG.remove();
  if (!hoverRimStagingG.node()?.childElementCount) hoverRimStagingG.remove();

  const labelG = contentG.append('g').attr('pointer-events', 'none');
  const chipParent = labelG as unknown as ChipParentSelection;
  const labelStatusG = contentG.append('g').attr('pointer-events', 'none');
  const statusChipParent = labelStatusG as unknown as ChipParentSelection;

  d3root.descendants().forEach((d) => {
    if (!HIERARCHY_TYPES.has(d.data.type)) return;
    if (d.data.type === 'status') return;
    appendBottomSizeChip(chipParent, d);
    appendChip(chipParent, {
      cx: d.x,
      cy: d.y - d.r,
      anchorR: d.r,
      linkedNode: d,
      chipKind: 'name',
      text: d.data.type === 'ec2' ? ec2InstancesLabel : d.data.name,
      svgIcon: d.data.type,
      bg: d.data.type === 'account' ? s.chip.accountNameBg : s.chip.bg,
      fg: s.chip.text.name,
      border: s.chip.border,
      fontSize: chipLayout.fontSize,
      fontWeight: chipLayout.fontWeight,
      sizingMode: 'outside',
      minR: chipLayout.name.minR,
      maxWidth: chipLayout.name.maxWidth,
    });
  });

  d3root.descendants().forEach((d) => {
    if (!usesOutsideStatusChips(d)) return;
    appendBottomSizeChip(chipParent, d);
    appendChip(chipParent, {
      cx: d.x,
      cy: d.y - d.r,
      anchorR: d.r,
      linkedNode: d,
      chipKind: 'name',
      text: d.data.name,
      bg: s.chip.bg,
      fg: s.chip.text.name,
      border: s.chip.border,
      fontSize: chipLayout.fontSize,
      fontWeight: chipLayout.fontWeight,
      sizingMode: 'outside',
      minR: chipLayout.name.minR,
      maxWidth: chipLayout.name.maxWidth,
    });
  });

  const infraNodeIndex = new Map<PackLayoutNode, InfraVisualSet>();
  _bubbleByNode.forEach((circleEl, node) => {
    if (!isInfraHierarchyType(node.data.type)) return;
    infraNodeIndex.set(node, {
      circle: circleEl as SVGCircleElement,
      nameChip: null,
      sizeChip: null,
    });
  });
  (contentG.selectAll('.b-chip') as unknown as Selection<Element, unknown, Element, unknown>).each(
    function (this: Element) {
      const chip = this as ChipElement;
      const meta = chip.__chipMeta;
      if (!meta?._linkedNode || !meta._chipKind) return;
      const set = infraNodeIndex.get(meta._linkedNode);
      if (!set) return;
      if (meta._chipKind === 'name') set.nameChip = chip;
      if (meta._chipKind === 'size') set.sizeChip = chip;
    }
  );
  (container as BubbleInfraContainer).__infraNodeIndex = infraNodeIndex;
  infraNodeIndex.forEach((set) => {
    if (set.nameChip) set.nameChip.setAttribute('filter', s.bubble.shadowFilter.default);
    if (set.sizeChip) set.sizeChip.setAttribute('filter', s.bubble.shadowFilter.default);
  });

  d3root.descendants().forEach((d) => {
    if (d.data.type !== 'status' && d.data.type !== 'reason') return;
    if (usesOutsideStatusChips(d)) return;
    const baseLabel = d.data.name;
    const volCount = d.data.count != null ? d.data.count : d.leaves ? d.leaves().length : 0;
    appendChip(statusChipParent, {
      cx: d.x,
      cy: d.y,
      anchorR: d.r,
      linkedNode: d,
      chipKind: 'center',
      text: baseLabel,
      lines: [
        { text: baseLabel, fontSize: chipLayout.fontSize, fontWeight: chipLayout.fontWeight, fg: s.chip.text.name },
        {
          text: formatBytes(d.data.value),
          fontSize: 11,
          fontWeight: 400,
          fg: s.chip.text.size,
        },
        {
          text: `(${(volCount || 0).toLocaleString()})`,
          fontSize: 11,
          fontWeight: 400,
          fg: s.chip.text.size,
        },
      ],
      plain: true,
      fg: s.chip.text.name,
      fontSize: chipLayout.fontSize,
      fontWeight: 600,
      sizingMode: 'inside',
      minR: 22,
      maxWidth: 280,
    });
  });

  const volumeLabels = createBubbleVolumeLabelLayer({
    invFit,
    labelG: labelG as unknown as LabelGroupSelection,
    d3root,
    getHierChipIconNode,
  });

  const minVolumeRadius = (() => {
    let minR: number | null = null;
    for (const d of d3root.descendants()) {
      if (d.data.type !== 'volume' || !(d.r > 0)) continue;
      minR = minR == null ? d.r : Math.min(minR, d.r);
    }
    return minR;
  })();

  const { kMin, kMax, fitTransform } = computeBubbleScaleExtent({
    viewBoxSize: size,
    rootRadius: d3root.r || size / 2,
    rootX: d3root.x ?? size / 2,
    rootY: d3root.y ?? size / 2,
    minVolumeRadius,
    invFit,
  });

  function applyZoomTransform(transform: ZoomTransform) {
    contentG.attr('transform', transform.toString());
    applyTextCounterScale(transform.k);
    applyChipLayoutForK(transform.k);
    volumeLabels.applyVolumeLabelsForK(transform.k);
    applyHaloLayoutForK(transform.k);
    options.onZoomLevel?.({
      pct: Math.round(transform.k * 100),
      canZoomIn: zoomCanZoomIn(transform.k, kMax),
      canZoomOut: zoomCanZoomOut(transform.k, kMin),
    });
  }

  const savedTransformRaw = getSavedZoomTransform(currentZoomKey);
  const savedTransform = savedTransformRaw
    ? clampZoomTransform(savedTransformRaw, kMin, kMax)
    : undefined;
  if (!savedTransform) {
    volumeLabels.applyVolumeLabelsForK(1);
  }

  bubbleZoom = zoom<SVGSVGElement, unknown>()
    .filter((event) => bubbleZoomDragFilter(event))
    .scaleExtent([kMin, kMax])
    .on('zoom', (e) => {
      applyZoomTransform(e.transform);
      setZoomCache(currentZoomKey, e.transform);
    });
  svg.call(bubbleZoom as (sel: typeof svg) => void).on('dblclick.zoom', null);
  // The wheel only ever zooms, anchored at the cursor; panning is left/right drag.
  // preventDefault also stops the `overflow: auto` canvas wrapper from native-scrolling.
  svg.on('wheel.bubbleZoom', (event: WheelEvent) => {
    if (!bubbleZoom) return;
    event.preventDefault();
    const p = pointer(event, svg.node());
    bubbleZoom.scaleBy(svg, wheelZoomFactor(event), p);
  });
  bubbleSvg = svg;

  if (savedTransform) {
    select(svg.node()!).call(bubbleZoom!.transform, savedTransform);
  } else {
    requestAnimationFrame(() => {
      applyZoomTransform(zoomTransform(svg.node()!));
    });
  }

  rememberRenderZoomKey(container, currentZoomKey);

  const overflowLineGap = 2;
  const overflowIconTextGap = volumeBubbleLabelLayout.iconTextGap;
  const overflowLineHeight = (fontSize: number) => fontSize * 1.15;
  // Match the name/size typography used elsewhere on the canvas (chip name line,
  // volume bubble size line) rather than scaling font size off the bubble radius.
  const overflowNameFontSize = chipLayout.fontSize;
  const overflowNameFontWeight = chipLayout.fontWeight;
  const overflowSizeFontSize = 11;
  const overflowSizeFontWeight = 400;

  d3root.descendants().forEach((d) => {
    if (!d.data._isOverflow) return;
    if (d.r < 14) return;

    const nameText = `+ ${d.data._hidden ?? 0} More`;
    // Reuse the same chord-fit truncation/progressive-drop the status/reason
    // center label uses, instead of drawing the text unbounded — a bubble this
    // small can't fit "+ 1,234 More" at 12px without it spilling past the rim.
    const layout = resolveInsideCenterLabelLayout({
      lines: [
        {
          text: nameText,
          fontSize: overflowNameFontSize,
          fontWeight: overflowNameFontWeight,
          fg: vb.overflow.labelFill,
        },
        {
          text: formatBytes(d.data.value),
          fontSize: overflowSizeFontSize,
          fontWeight: overflowSizeFontWeight,
          fg: vb.overflow.sizeFill,
        },
      ],
      bubbleScreenDiam: 2 * d.r,
      measureWidth: measureTextWidth,
      minTextChars: chipLayout.minTextChars,
    });
    if (!layout.show) return;

    const visibleLines = layout.visibleLines;
    const showIcon = d.r >= 22 && visibleLines.length > 1;
    const iconSize = Math.min(16, Math.max(10, d.r * 0.32));
    const lineHeights = visibleLines.map((line) => overflowLineHeight(line.fontSize));
    const textH =
      lineHeights.reduce((sum, h) => sum + h, 0) + overflowLineGap * (visibleLines.length - 1);
    const totalH = showIcon ? iconSize + overflowIconTextGap + textH : textH;

    let cursorY = d.y - totalH / 2;

    if (showIcon) {
      const iconNode = getHierChipIconNode('volume');
      if (iconNode) {
        iconNode.setAttribute('x', String(d.x - iconSize / 2));
        iconNode.setAttribute('y', String(cursorY));
        iconNode.setAttribute('width', String(iconSize));
        iconNode.setAttribute('height', String(iconSize));
        labelG.node()?.appendChild(iconNode);
      }
      cursorY += iconSize + overflowIconTextGap;
    }

    visibleLines.forEach((line, i) => {
      labelG
        .append('text')
        .attr('x', d.x)
        .attr('y', cursorY + line.fontSize * 0.85)
        .attr('text-anchor', 'middle')
        .attr('font-size', line.fontSize)
        .attr('font-weight', line.fontWeight)
        .attr('fill', line.fg)
        .attr('pointer-events', 'none')
        .text(line.text);
      cursorY += lineHeights[i]! + overflowLineGap;
    });
  });

  const labelRestoreTimer = runBubbleFlipTransition({
    useTransition,
    posCache,
    labelG: labelG as unknown as LabelGroupSelection,
    bubbleG: bubbleG as unknown as CircleNodeSelection,
    haloSel: haloSel as unknown as CircleNodeSelection,
    hoverRimSel: hoverRimSel as unknown as CircleNodeSelection,
    managedSel: managedSel as unknown as CircleNodeSelection,
    currentZoomK,
    effectiveHaloWidth,
    hoverRimRadius,
    HALO_WIDTH_LOW,
    managedInnerRatio,
  });

  if (options.zoomControlsRef) {
    options.zoomControlsRef.current = createBubbleZoomControls(bubbleZoom, bubbleSvg, {
      onZoomFit: () => deleteZoomCache(currentZoomKey),
      fitTransform,
    });
  }

  const emphasisContainer = container as BubbleEmphasisContainer;
  emphasisContainer.__bubbleChipRelayout = () => applyChipLayoutForK(currentZoomK());

  const notifyRenderComplete = () => options.onRenderComplete?.();
  let renderCompleteTimer: ReturnType<typeof setTimeout> | undefined;
  if (useTransition) {
    renderCompleteTimer = setTimeout(notifyRenderComplete, bubbleTransitionMs);
  } else {
    notifyRenderComplete();
  }

  return () => {
    clearBubbleCanvasDrillUpClass(container);
    if (renderCompleteTimer != null) clearTimeout(renderCompleteTimer);
    if (labelRestoreTimer != null) {
      clearTimeout(labelRestoreTimer);
      labelG.style('display', '');
    }
    delete emphasisContainer.__bubbleChipRelayout;
    select(container).selectAll('*').interrupt();
    clearZoomControls();
  };
}
